import { useState } from "react";
import { arrayOf, bool, func, number, object, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  changeCompanyMcpSsoAccessQuery,
  editTeamMemberQuery,
  removeTeamMemberQuery,
  resendCompanyMcpSsoLinkEmailQuery,
  resetCompanyMcpSsoIdentityQuery,
  retryCompanyMcpSsoAccessQuery,
} from "@query";
import {
  canManageMcpSso,
  canRetryMcpSso,
  canSaveMcpSsoLevel,
  canSubmitMcpSsoReset,
  MCP_SSO_RESET_CONFIRMATION,
  MCP_SSO_REVOKE_CONFIRMATION,
} from "@utils";
import { useStores } from "@hooks";
import { getApiErrorMessage } from "@client";
import { TeamUserInfoForm } from "@forms";
import { Alert, Button, Dialog, MenuButton, PopMenu, TextField } from "@components";
import { CircularProgress, IconButton, MenuItem } from "@material-ui/core";
import { Pencil, Trash } from "@assets";
import { MoreVert, VpnKey } from "@material-ui/icons";

export const MemberMenu = observer(({
  userUuid,
  memberId,
  name,
  email,
  professionalLevels,
  stackTypes,
  teamRoles,
  professionalLevel,
  technicalStack,
  teamRole,
  projectsVisibility,
  isAdmin: isUserAdmin,
  reloadData,
  mcpSsoAccess,
  // totalAdmins=1,
}) => {
  const { t } = useTranslation();
  const { userStore } = useStores();

  const [ anchorEl, setAnchorEl ] = useState(null);
  const [ visible, show ] = useState(false);
  const [ editionModalVisible, openEditionModal ] = useState(false);
  const [ removeAlertVisible, openRemoveAlert ] = useState(false);
  const [ alertLoading, setAlertLoading ] = useState(false);
  const [ mcpDialogVisible, setMcpDialogVisible ] = useState(false);
  const [ mcpLevel, setMcpLevel ] = useState(
    mcpSsoAccess?.accessLevel || "none",
  );
  const [ mcpConfirmation, setMcpConfirmation ] = useState("");
  const [ mcpSaving, setMcpSaving ] = useState(false);
  const [ mcpError, setMcpError ] = useState();
  const [ mcpResetVisible, setMcpResetVisible ] = useState(false);
  const [ mcpResetConfirmation, setMcpResetConfirmation ] = useState("");

  const {isAdmin, isManager, data} = userStore;

  const saveMemberData = async ({
    teamRole,
                                  technicalStack,
    professionalLevel,
    projectsVisibility,
  }) => {
    await editTeamMemberQuery(
      userUuid,
      teamRole,
      professionalLevel,
      technicalStack,
      projectsVisibility,
    );
    openEditionModal(false);
    show(false);
    reloadData(email);
  };

  const removeMember = async () => {
    setAlertLoading(true);
    await removeTeamMemberQuery(userUuid);
    openRemoveAlert(false);
    setAlertLoading(false);
    reloadData();
  };

  const handleRemove = () => {
    openRemoveAlert(true);
  };

  const handleEdit = async () => {
    openEditionModal(true);
  };

  const closeEditionModal = () => {
    openEditionModal(false);
  };

  const handleDialog = () => {
    isManager ? show(!visible) : handleEdit();
  };

  const openMcpDialog = () => {
    setMcpLevel(mcpSsoAccess?.accessLevel || "none");
    setMcpConfirmation("");
    setMcpDialogVisible(true);
    show(false);
  };

  const saveMcpAccess = async () => {
    setMcpSaving(true);
    try {
      await changeCompanyMcpSsoAccessQuery(userUuid, mcpLevel);
      setMcpDialogVisible(false);
      await reloadData(email);
    } catch (error) {
      setMcpError(getApiErrorMessage(error, t("errors.network")));
    } finally {
      setMcpSaving(false);
    }
  };

  const retryMcpAccess = async () => {
    setMcpSaving(true);
    try {
      await retryCompanyMcpSsoAccessQuery(userUuid);
      show(false);
      await reloadData(email);
    } catch (error) {
      setMcpError(getApiErrorMessage(error, t("errors.network")));
    } finally {
      setMcpSaving(false);
    }
  };

  const resetMcpIdentity = async () => {
    setMcpSaving(true);
    try {
      await resetCompanyMcpSsoIdentityQuery(userUuid, mcpResetConfirmation);
      setMcpResetVisible(false);
      await reloadData(email);
    } catch (error) {
      setMcpError(getApiErrorMessage(error, t("errors.network")));
    } finally {
      setMcpSaving(false);
    }
  };

  const resendMcpLink = async () => {
    setMcpSaving(true);
    try {
      await resendCompanyMcpSsoLinkEmailQuery(userUuid);
      show(false);
    } catch (error) {
      setMcpError(getApiErrorMessage(error, t("errors.network")));
    } finally {
      setMcpSaving(false);
    }
  };

  return (
    <>
      <IconButton
        color="primary"
        ref={setAnchorEl}
        onClick={handleDialog}
        aria-label="menu"
      >
        {isManager ? <MoreVert/> : <Pencil/>}
      </IconButton>
      <PopMenu
        key={`${memberId}popmenu`}
        show={visible}
        anchor={anchorEl}
        onClickAway={() => show(false)}
        className="min-h-max"
        style={{width: 230}}
        placement="bottom-end"
      >
        <MenuButton icon={<Pencil/>} onClick={handleEdit}>
          {t("common.edit")}
        </MenuButton>
        {
          canManageMcpSso(isAdmin) &&
          <MenuButton icon={<VpnKey/>} onClick={openMcpDialog}>
            {t("views.team.mcp_sso.manage")}
          </MenuButton>
        }
        {
          canManageMcpSso(isAdmin) && canRetryMcpSso(mcpSsoAccess) &&
          <MenuButton
            icon={mcpSaving ? <CircularProgress size={18} /> : <VpnKey/>}
            onClick={retryMcpAccess}
          >
            {t("views.team.mcp_sso.retry")}
          </MenuButton>
        }
        {
          canManageMcpSso(isAdmin) && (
            mcpSsoAccess?.connected
            || mcpSsoAccess?.syncErrorCode === "reset_cleanup_pending"
          ) &&
          <MenuButton
            icon={<VpnKey/>}
            onClick={() => {
              setMcpResetConfirmation("");
              setMcpResetVisible(true);
              show(false);
            }}
          >
            {t("views.team.mcp_sso.reset")}
          </MenuButton>
        }
        {
          canManageMcpSso(isAdmin) &&
          mcpSsoAccess?.status === "awaiting_link" &&
          <MenuButton
            icon={mcpSaving ? <CircularProgress size={18} /> : <VpnKey/>}
            onClick={resendMcpLink}
          >
            {t("views.team.mcp_sso.resend")}
          </MenuButton>
        }
        {
          email !== data.email && (isAdmin || !isUserAdmin) &&
          <MenuButton icon={<Trash color="error"/>} onClick={handleRemove}>
            {t("common.delete")}
          </MenuButton>
        }
      </PopMenu>
      {
        editionModalVisible &&
        <Dialog
          open={editionModalVisible}
          width={420}
          onClose={closeEditionModal}
          title={t("views.team.modal_title")}
        >
          <TeamUserInfoForm
            onSubmit={saveMemberData}
            onCancel={closeEditionModal}
            allowRoleChange={isManager}
            professionalLevels={professionalLevels}
            stackTypes={stackTypes}
            teamRoles={teamRoles}
            defaultValues={{
              professionalLevel,
              teamRole,
              technicalStack,
              projectsVisibility: projectsVisibility || "all",
            }}
          />
        </Dialog>
      }
      {mcpDialogVisible && (
        <Dialog
          open={mcpDialogVisible}
          width={480}
          onClose={() => setMcpDialogVisible(false)}
          title={t("views.team.mcp_sso.manage_for", {name})}
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => setMcpDialogVisible(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveMcpAccess}
                disabled={
                  mcpSaving || !canSaveMcpSsoLevel(mcpLevel, mcpConfirmation)
                }
                endIcon={mcpSaving && <CircularProgress size={18} />}
              >
                { t("common.save")  }
              </Button>
            </>
          }
        >
          <TextField
            select
            fullWidth
            label={t("views.team.mcp_sso.level")}
            value={mcpLevel}
            onChange={event => setMcpLevel(event.target.value)}
          >
            {["none", "mcp:read", "mcp:write"].map(level => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </TextField>
          {
            mcpLevel === "none" &&
            <TextField
              fullWidth
              className="mt-4"
              label={t("views.team.mcp_sso.revoke_confirmation")}
              value={mcpConfirmation}
              onChange={event => setMcpConfirmation(event.target.value)}
              helperText={t("views.team.mcp_sso.confirmation_hint", {
                confirmation: MCP_SSO_REVOKE_CONFIRMATION,
              })}
            />
          }
        </Dialog>
      )}
      {
        mcpResetVisible &&
        <Dialog
          open={mcpResetVisible}
          width={480}
          onClose={() => setMcpResetVisible(false)}
          title={t("views.team.mcp_sso.reset_for", {name})}
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => setMcpResetVisible(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={resetMcpIdentity}
                disabled={
                  mcpSaving || !canSubmitMcpSsoReset(mcpResetConfirmation)
                }
                endIcon={mcpSaving && <CircularProgress size={18} />}
              >
                { t("views.team.mcp_sso.reset") }
              </Button>
            </>
          }
        >
          <p>{t("views.team.mcp_sso.reset_warning")}</p>
          <TextField
            fullWidth
            value={mcpResetConfirmation}
            onChange={event => setMcpResetConfirmation(event.target.value)}
            helperText={t("views.team.mcp_sso.confirmation_hint", {
              confirmation: MCP_SSO_RESET_CONFIRMATION,
            })}
          />
        </Dialog>
      }
      <Alert
        isOpen={Boolean(mcpError)}
        title={mcpError}
        acceptText={t("common.close")}
        onAccept={() => setMcpError(undefined)}
      />
      {
        removeAlertVisible &&
        <Alert
          isOpen={removeAlertVisible}
          onAccept={removeMember}
          onCancel={() => openRemoveAlert(false)}
          title={t("views.team.remove_member", {name})}
          isAcceptLoading={alertLoading}
          acceptText={t("common.delete")}
        />
      }
    </>
  );
});

MemberMenu.propTypes = {
  userUuid: string.isRequired,
  memberId: number.isRequired,
  email: string.isRequired,
  name: string.isRequired,
  isAdmin: bool.isRequired,
  allowRoleChange: bool,
  reloadData: func.isRequired,
  totalAdmins: number,
  professionalLevel: number,
  technicalStack: arrayOf(number),
  teamRole: number,
  projectsVisibility: string,
  professionalLevels: arrayOf(object),
  stackTypes: arrayOf(object),
  teamRoles: arrayOf(object),
  mcpSsoAccess: object,
};
